/**
 * Employee Directory — search + department filter + status filter + grid/list toggle.
 */

import * as React from 'react';
import { useList } from '@refinedev/core';
import { useNavigate } from 'react-router';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Card,
  CardContent,
  Avatar,
  AvatarFallback,
  StatusBadge,
  EmptyState,
  Badge,
} from '@yesbos/ui';
import { Plus, Search, Users, LayoutGrid, List } from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────────────────

interface Employee {
  id: string;
  first_name: string;
  last_name?: string;
  full_name?: string;
  email?: string;
  phone?: string;
  employment_status: string;
  employee_code?: string;
  department_id?: string;
  designation_id?: string;
  department_name?: string;
  designation_name?: string;
}

interface Department {
  id: string;
  name: string;
}

// ── Employee Card ─────────────────────────────────────────────────────────────

function EmployeeCard({ emp, onClick }: { emp: Employee; onClick: () => void }) {
  const name = emp.full_name ?? `${emp.first_name} ${emp.last_name ?? ''}`.trim();
  const initials = name.split(' ').map((p) => p[0]).join('').toUpperCase().slice(0, 2);

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
    >
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start gap-3">
          <Avatar className="h-10 w-10 flex-shrink-0">
            <AvatarFallback className="bg-primary text-primary-foreground text-sm font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{name}</p>
            {emp.designation_name && (
              <p className="text-xs text-muted-foreground truncate">{emp.designation_name}</p>
            )}
            {emp.department_name && (
              <Badge variant="outline" className="text-xs mt-1">{emp.department_name}</Badge>
            )}
          </div>
          <StatusBadge status={emp.employment_status} />
        </div>
        {emp.email && (
          <p className="text-xs text-muted-foreground mt-2 truncate">{emp.email}</p>
        )}
        {emp.employee_code && (
          <p className="text-xs text-muted-foreground">#{emp.employee_code}</p>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function EmployeeListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = React.useState('');
  const [deptFilter, setDeptFilter] = React.useState('all');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [viewMode, setViewMode] = React.useState<'grid' | 'list'>('grid');

  // Build filters for Refine
  const filters = React.useMemo(() => {
    const f: { field: string; operator: string; value: unknown }[] = [];
    if (deptFilter !== 'all') {
      f.push({ field: 'department_id', operator: 'eq', value: deptFilter });
    }
    if (statusFilter !== 'all') {
      f.push({ field: 'employment_status', operator: 'eq', value: statusFilter });
    }
    if (search.trim()) {
      f.push({ field: 'full_name', operator: 'contains', value: search.trim() });
    }
    return f;
  }, [deptFilter, statusFilter, search]);

  const { data, isLoading } = useList<Employee>({
    resource: 'employees',
    filters,
    pagination: { pageSize: 50 },
    sorters: [{ field: 'first_name', order: 'asc' }],
  });

  const { data: deptData } = useList<Department>({
    resource: 'departments',
    pagination: { pageSize: 100 },
  });

  const employees = (data?.data ?? []) as Employee[];
  const departments = (deptData?.data ?? []) as Department[];

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Employee Directory</h1>
          {!isLoading && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {data?.total ?? employees.length} employee{(data?.total ?? employees.length) !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <Button onClick={() => navigate('/employees/create')}>
          <Plus className="h-4 w-4 mr-2" /> Add Employee
        </Button>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search employees…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Department" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {departments.map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="probation">Probation</SelectItem>
            <SelectItem value="notice_period">Notice Period</SelectItem>
            <SelectItem value="exited">Exited</SelectItem>
          </SelectContent>
        </Select>

        <div className="ml-auto flex gap-1">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="icon"
            onClick={() => setViewMode('grid')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="icon"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className={`grid gap-4 ${viewMode === 'grid' ? 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'}`}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-28 rounded-lg border bg-muted animate-pulse" />
          ))}
        </div>
      ) : employees.length === 0 ? (
        <EmptyState
          icon={<Users className="h-8 w-8" />}
          title="No Employees Found"
          description={search || deptFilter !== 'all' || statusFilter !== 'all'
            ? 'Try adjusting your filters.'
            : 'Add your first employee to get started.'}
          action={!search && deptFilter === 'all' && statusFilter === 'all'
            ? { label: 'Add Employee', onClick: () => navigate('/employees/create') }
            : undefined}
        />
      ) : (
        <div className={`grid gap-4 ${viewMode === 'grid' ? 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1 max-w-3xl'}`}>
          {employees.map((emp) => (
            <EmployeeCard
              key={emp.id}
              emp={emp}
              onClick={() => navigate(`/employees/${emp.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default EmployeeListPage;
